import { useEffect, useState } from "react";
import { Button } from "@electro-planner/ui";

export type InspectorEdge = {
  id: string;
  area?: number;
  color?: string;
  circuitLabel?: string;
  cableDesignation?: string;
  cableId?: string;
  cableType?: string;
  source: string;
  target: string;
};

type Props = {
  edge: InspectorEdge | null;
  onSetArea: (id: string, area: number | null) => void;
  onSetColor: (id: string, color: string | null) => void;
  onSetCircuitLabel: (id: string, label: string | null) => void;
  /**
   * Skapar en ny kabel innehållande bara denna conductor. Auto-numret
   * (W1/W2/…) tilldelas i mutations-lagret.
   */
  onCreateCable: (id: string) => void;
  /** Plockar ut denna conductor ur sin nuvarande kabel. */
  onLeaveCable: (id: string) => void;
  /** Byter namn på aktuell kabel. No-op om tom. */
  onRenameCable: (id: string, designation: string) => void;
  /** Sätter kabeltyp (fritext, t.ex. "EKK 3G1.5"). Tom sträng rensar typen. */
  onSetCableType: (id: string, type: string) => void;
  onDelete: (id: string) => void;
};

// Svenska standardval för ledartvärsnitt i starkströmsinstallation.
// Användaren kan skriva in andra värden via fritext i inspektören.
const AREA_PRESETS: Array<{ value: number; label: string }> = [
  { value: 1.5, label: "1.5 mm²" },
  { value: 2.5, label: "2.5 mm²" },
  { value: 4, label: "4 mm²" },
  { value: 6, label: "6 mm²" },
  { value: 10, label: "10 mm²" },
  { value: 16, label: "16 mm²" },
];

// IEC 60446 / SS-EN 60446 färgkoder — samma lista som edge-renderingen förstår.
// Label visar bokstavskod + svenskt namn; swatch visar renderad färg.
const COLOR_PRESETS: Array<{ code: string; label: string; swatch: string }> = [
  { code: "BN", label: "BN · brun", swatch: "#8b4513" },
  { code: "BK", label: "BK · svart", swatch: "#111827" },
  { code: "GY", label: "GY · grå", swatch: "#6b7280" },
  { code: "BU", label: "BU · blå", swatch: "#1d4ed8" },
  { code: "GNYE", label: "GNYE · grön-gul", swatch: "#16a34a" },
  { code: "RD", label: "RD · röd", swatch: "#dc2626" },
  { code: "YE", label: "YE · gul", swatch: "#ca8a04" },
  { code: "WH", label: "WH · vit", swatch: "#9ca3af" },
  { code: "OG", label: "OG · orange", swatch: "#ea580c" },
];

/**
 * Högerpanel för en markerad tråd (Conductor). Fas 1 exponerar tvärsnittsarea
 * och IEC-färgkod; kretsar/cables hanteras senare i Fas 2.
 */
export function EdgeInspector({
  edge,
  onSetArea,
  onSetColor,
  onSetCircuitLabel,
  onCreateCable,
  onLeaveCable,
  onRenameCable,
  onSetCableType,
  onDelete,
}: Props) {
  // Fritext för area — låter användaren mata in värden som inte finns i presets
  // (t.ex. 0.75 för signalkabel). Sync:as från edge.id så byte av markering
  // skriver över draft utan att trampa pågående redigering.
  const [areaDraft, setAreaDraft] = useState<string>(
    edge?.area != null ? String(edge.area) : "",
  );
  const [colorDraft, setColorDraft] = useState<string>(edge?.color ?? "");
  const [circuitDraft, setCircuitDraft] = useState<string>(edge?.circuitLabel ?? "");
  const [cableDraft, setCableDraft] = useState<string>(edge?.cableDesignation ?? "");
  const [cableTypeDraft, setCableTypeDraft] = useState<string>(edge?.cableType ?? "");

  useEffect(() => {
    setAreaDraft(edge?.area != null ? String(edge.area) : "");
    setColorDraft(edge?.color ?? "");
    setCircuitDraft(edge?.circuitLabel ?? "");
    setCableDraft(edge?.cableDesignation ?? "");
    setCableTypeDraft(edge?.cableType ?? "");
  }, [
    edge?.id,
    edge?.area,
    edge?.color,
    edge?.circuitLabel,
    edge?.cableDesignation,
    edge?.cableType,
  ]);

  function commitArea() {
    if (!edge) return;
    const trimmed = areaDraft.trim().replace(",", ".");
    if (trimmed === "") {
      if (edge.area != null) onSetArea(edge.id, null);
      return;
    }
    const n = Number.parseFloat(trimmed);
    if (!Number.isFinite(n) || n <= 0) {
      setAreaDraft(edge.area != null ? String(edge.area) : "");
      return;
    }
    if (n === edge.area) return;
    onSetArea(edge.id, n);
  }

  function commitColor() {
    if (!edge) return;
    const trimmed = colorDraft.trim();
    if (trimmed === "") {
      if (edge.color) onSetColor(edge.id, null);
      return;
    }
    if (trimmed === edge.color) return;
    onSetColor(edge.id, trimmed);
  }

  function commitCircuit() {
    if (!edge) return;
    const trimmed = circuitDraft.trim();
    if (trimmed === "") {
      if (edge.circuitLabel) onSetCircuitLabel(edge.id, null);
      return;
    }
    if (trimmed === edge.circuitLabel) return;
    onSetCircuitLabel(edge.id, trimmed);
  }

  function commitCableRename() {
    if (!edge) return;
    if (!edge.cableDesignation) return;
    const trimmed = cableDraft.trim();
    if (trimmed === "" || trimmed === edge.cableDesignation) {
      setCableDraft(edge.cableDesignation ?? "");
      return;
    }
    onRenameCable(edge.id, trimmed);
  }

  function commitCableType() {
    if (!edge) return;
    if (!edge.cableId) return;
    const trimmed = cableTypeDraft.trim();
    if (trimmed === (edge.cableType ?? "")) return;
    onSetCableType(edge.id, trimmed);
  }

  return (
    <aside className="no-print flex w-72 shrink-0 flex-col border-l border-[var(--color-border)] bg-[var(--color-surface)]">
      <div className="border-b border-[var(--color-border)] px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
          Tråd
        </h2>
      </div>

      {!edge ? (
        <div className="px-3 py-6 text-sm text-[var(--color-muted)]">
          Markera en tråd för att redigera krets, area och färg.
        </div>
      ) : (
        <div className="flex flex-col gap-4 px-3 py-3 text-sm">
          <Field label="Krets (fri text)">
            <input
              type="text"
              value={circuitDraft}
              onChange={(e) => setCircuitDraft(e.target.value)}
              onBlur={commitCircuit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
                if (e.key === "Escape") {
                  setCircuitDraft(edge.circuitLabel ?? "");
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder="t.ex. F1, F1:L1, -F1"
              className="w-full rounded border bg-white px-2 py-1 text-sm outline-none focus:ring-2"
              style={{ borderColor: "var(--color-border)" }}
            />
          </Field>

          <Field label="Kabel">
            {edge.cableDesignation ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={cableDraft}
                    onChange={(e) => setCableDraft(e.target.value)}
                    onBlur={commitCableRename}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                      }
                      if (e.key === "Escape") {
                        setCableDraft(edge.cableDesignation ?? "");
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    aria-label="Kabelbeteckning"
                    className="w-full rounded border bg-white px-2 py-1 text-sm outline-none focus:ring-2"
                    style={{ borderColor: "var(--color-border)" }}
                  />
                </div>
                <input
                  type="text"
                  value={cableTypeDraft}
                  onChange={(e) => setCableTypeDraft(e.target.value)}
                  onBlur={commitCableType}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    }
                    if (e.key === "Escape") {
                      setCableTypeDraft(edge.cableType ?? "");
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  aria-label="Kabeltyp"
                  placeholder="Typ, t.ex. EKK 3G1.5"
                  className="w-full rounded border bg-white px-2 py-1 text-xs outline-none focus:ring-2"
                  style={{ borderColor: "var(--color-border)" }}
                />
                <button
                  type="button"
                  onClick={() => onLeaveCable(edge.id)}
                  className="self-start rounded text-xs text-[var(--color-muted)] underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]"
                >
                  Bryt ut ur kabel
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCreateCable(edge.id)}
                  title="Skapa en ny kabel med denna tråd"
                >
                  Gruppera som ny kabel
                </Button>
                <p className="text-xs text-[var(--color-muted)]">
                  Markera flera trådar och använd kommandopaletten för att samla
                  dem i samma kabel.
                </p>
              </div>
            )}
          </Field>

          <Field label="Tvärsnitt (mm²)">
            <div className="flex flex-wrap gap-1">
              {AREA_PRESETS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => {
                    onSetArea(edge.id, p.value);
                    setAreaDraft(String(p.value));
                  }}
                  aria-pressed={edge.area === p.value}
                  className="rounded border px-2 py-1 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]"
                  style={{
                    background: edge.area === p.value ? "#0f766e" : "white",
                    color: edge.area === p.value ? "white" : "var(--color-fg)",
                    borderColor:
                      edge.area === p.value ? "#0f766e" : "var(--color-border)",
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              inputMode="decimal"
              value={areaDraft}
              onChange={(e) => setAreaDraft(e.target.value)}
              onBlur={commitArea}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
                if (e.key === "Escape") {
                  setAreaDraft(edge.area != null ? String(edge.area) : "");
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder="Eller skriv eget värde"
              className="mt-2 w-full rounded border bg-white px-2 py-1 text-sm outline-none focus:ring-2"
              style={{ borderColor: "var(--color-border)" }}
            />
          </Field>

          <Field label="IEC-färgkod">
            <div className="grid grid-cols-3 gap-1">
              {COLOR_PRESETS.map((p) => {
                const active =
                  edge.color && edge.color.toUpperCase() === p.code;
                return (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => {
                      onSetColor(edge.id, p.code);
                      setColorDraft(p.code);
                    }}
                    aria-pressed={Boolean(active)}
                    title={p.label}
                    className="flex items-center gap-1 rounded border px-1 py-1 text-[10px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]"
                    style={{
                      background: active ? "#0f766e" : "white",
                      color: active ? "white" : "var(--color-fg)",
                      borderColor: active ? "#0f766e" : "var(--color-border)",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="inline-block h-3 w-3 shrink-0 rounded-sm border border-black/10"
                      style={{ background: p.swatch }}
                    />
                    <span className="truncate">{p.code}</span>
                  </button>
                );
              })}
            </div>
            <input
              type="text"
              value={colorDraft}
              onChange={(e) => setColorDraft(e.target.value)}
              onBlur={commitColor}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
                if (e.key === "Escape") {
                  setColorDraft(edge.color ?? "");
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder="Eller skriv egen kod/hex"
              className="mt-2 w-full rounded border bg-white px-2 py-1 text-sm outline-none focus:ring-2"
              style={{ borderColor: "var(--color-border)" }}
            />
            {edge.color ? (
              <button
                type="button"
                onClick={() => {
                  onSetColor(edge.id, null);
                  setColorDraft("");
                }}
                className="mt-1 self-start rounded text-xs text-[var(--color-muted)] underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]"
              >
                Rensa färg
              </button>
            ) : null}
          </Field>

          <div className="mt-2 border-t border-[var(--color-border)] pt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(edge.id)}
              title="Radera tråd (Delete)"
            >
              Radera tråd
            </Button>
          </div>
        </div>
      )}
    </aside>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted)]">
        {label}
      </span>
      {children}
    </label>
  );
}
